<template>
  <modal :name="modalName" width="94%" height="auto" :max-width="620" :max-height="760"
    :adaptive="true" :scrollable="true" :click-to-close="false"
    @opened="focusClose" @before-close="beforeClose">
    <section ref="dialog" class="communication-plan" role="dialog" aria-modal="true"
      aria-labelledby="communication-plan-title" @keydown.tab="trapFocus" @keyup.esc.stop.prevent="close">
      <AppModalHeader ref="header" design="new" title="Plan Safari contacts"
        eyebrow="Ground Ops · Communication" title-id="communication-plan-title" @close="close" />
      <div class="communication-plan__body">
        <p>Create today's contact tasks: Welcome 3 days before arrival, Safari starts tomorrow the day before the tour, and an evening follow-up for today's tour guests. A missed Welcome can still be added before arrival. This does not send messages to guests.</p>
        <p v-if="working && !result" class="communication-plan__muted">Checking Safari groups…</p>
        <div v-if="error" class="communication-plan__error" role="alert">{{ error }}</div>
        <template v-if="result">
          <p class="communication-plan__status" role="status">
            {{ applied ? 'The contact plan was updated.' : 'Preview: these changes will be made when you apply the plan.' }}
          </p>
          <dl class="communication-plan__metrics">
            <div><dt>Safari groups checked</dt><dd>{{ result.groups }}</dd></div>
            <div><dt>New contacts</dt><dd>{{ result.created }}</dd></div>
            <div><dt>Contacts to update</dt><dd>{{ result.updated }}</dd></div>
            <div><dt>Contacts to cancel</dt><dd>{{ result.cancelled }}</dd></div>
          </dl>
          <p v-if="result.blocked" class="communication-plan__warning">
            {{ result.blocked }} contacts in this plan have no valid WhatsApp number.
          </p>
          <p v-else-if="!hasChanges && !applied" class="communication-plan__muted">All contact tasks are already up to date.</p>
        </template>
      </div>
      <footer class="communication-plan__footer">
        <AppButton design="new" theme="dark" :disabled="working" @click.native="close">
          {{ applied ? 'Close' : 'Cancel' }}
        </AppButton>
        <AppButton v-if="!applied" design="new" theme="info" :disabled="working" @click.native="preview">
          {{ working ? 'Checking…' : (result ? 'Refresh preview' : 'Preview') }}
        </AppButton>
        <AppButton v-if="result && !applied" design="new" theme="success"
          :disabled="working || !hasChanges" @click.native="apply">
          {{ working ? 'Applying…' : 'Apply plan' }}
        </AppButton>
      </footer>
    </section>
  </modal>
</template>

<script>
import { postSafariCommunicationPlan } from '@/http'
import { errorMessage } from './model'

export default {
  name: 'CommunicationPlanModal',
  data: () => ({
    modalName: 'ground-ops-communication-plan',
    result: null,
    applied: false,
    working: false,
    error: ''
  }),
  computed: {
    hasChanges() {
      return Boolean(this.result && (this.result.created || this.result.updated || this.result.cancelled))
    }
  },
  mounted() {
    this.$modal.show(this.modalName)
    this.preview()
  },
  methods: {
    focusClose() { this.$refs.header.focusClose() },
    close() { if (!this.working) this.$modal.hide(this.modalName) },
    beforeClose(event) {
      if (this.working) event.cancel()
      else this.$emit('close')
    },
    async preview() {
      if (this.working || this.applied) return
      this.working = true
      this.error = ''
      this.result = null
      try {
        this.result = (await postSafariCommunicationPlan(true)).data
      } catch (error) {
        this.error = errorMessage(error, 'Could not preview the contact plan. Please try again.')
      } finally {
        this.working = false
      }
    },
    async apply() {
      if (this.working || this.applied || !this.hasChanges) return
      this.working = true
      this.error = ''
      try {
        this.result = (await postSafariCommunicationPlan(false)).data
        this.applied = true
        this.$emit('completed')
      } catch (error) {
        this.error = errorMessage(error, 'Could not confirm the result. Check the queue and refresh the preview before trying again.')
      } finally {
        this.working = false
      }
    },
    trapFocus(event) {
      const elements = Array.from(this.$refs.dialog.querySelectorAll('button:not([disabled])'))
        .filter(element => element.getClientRects().length)
      if (!elements.length) return
      const first = elements[0]
      const last = elements[elements.length - 1]
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() } else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
    }
  }
}
</script>

<style scoped lang="scss">
.communication-plan { background: $ui-surface; color: $ui-text; font-family: $ui-font-body; }
.communication-plan__body { display: grid; gap: $ui-space-16; padding: $ui-space-24; }
.communication-plan__body p { margin: 0; line-height: 1.5; }
.communication-plan__muted { color: $ui-muted; }
.communication-plan__status { font-weight: 600; color: $ui-ink; }
.communication-plan__error, .communication-plan__warning { padding: $ui-space-12; border-radius: $ui-control-radius; background: $ui-amber-soft; color: $ui-ink; }
.communication-plan__error { background: $ui-red-soft; color: $ui-red; }
.communication-plan__metrics { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: $ui-space-12; margin: 0; }
.communication-plan__metrics div { padding: $ui-space-12; border-radius: $ui-control-radius; background: $ui-surface-soft; }
.communication-plan__metrics dt { color: $ui-muted; font-size: $ui-font-size-metadata; }
.communication-plan__metrics dd { margin: $ui-space-4 0 0; color: $ui-ink; font-family: $ui-font-heading; font-size: $ui-font-size-section-title; font-weight: 600; }
.communication-plan__footer { display: flex; justify-content: flex-end; gap: $ui-space-12; padding: $ui-space-16 $ui-space-24; border-top: 1px solid $ui-line; }
@media (max-width: 600px) {
  .communication-plan__body { padding: $ui-space-16; }
  .communication-plan__footer { flex-wrap: wrap; padding: $ui-space-16; }
}
</style>
